// Text diffs for traceability items: lines, and words within a changed line.

export type Op<T> = { op: "eq" | "del" | "ins"; v: T };
export type Part = { op: "eq" | "del" | "ins"; text: string };
export type Row =
  | { kind: "same" | "del" | "ins"; text: string }
  /** A line changed in place: its words, deleted and inserted. */
  | { kind: "mod"; parts: Part[] };

/** Longest-common-subsequence diff (fine for item-sized texts). */
export function diffSeq<T>(a: T[], b: T[]): Op<T>[] {
  // Trim the common ends first; most edits are small.
  let pre = 0;
  while (pre < a.length && pre < b.length && a[pre] === b[pre]) pre++;
  let suf = 0;
  while (suf < a.length - pre && suf < b.length - pre && a[a.length - 1 - suf] === b[b.length - 1 - suf]) suf++;
  const x = a.slice(pre, a.length - suf);
  const y = b.slice(pre, b.length - suf);
  const out: Op<T>[] = a.slice(0, pre).map((v) => ({ op: "eq", v }));
  if (x.length * y.length > 4_000_000) {
    // Too big to align: everything in between is replaced.
    out.push(...x.map((v) => ({ op: "del" as const, v })), ...y.map((v) => ({ op: "ins" as const, v })));
  } else {
    const w = y.length + 1;
    const L = new Uint32Array((x.length + 1) * w);
    for (let i = x.length - 1; i >= 0; i--) {
      for (let j = y.length - 1; j >= 0; j--) {
        L[i * w + j] = x[i] === y[j] ? L[(i + 1) * w + j + 1] + 1 : Math.max(L[(i + 1) * w + j], L[i * w + j + 1]);
      }
    }
    let i = 0;
    let j = 0;
    while (i < x.length || j < y.length) {
      if (i < x.length && j < y.length && x[i] === y[j]) {
        out.push({ op: "eq", v: x[i++] });
        j++;
      } else if (i < x.length && (j === y.length || L[(i + 1) * w + j] >= L[i * w + j + 1])) {
        out.push({ op: "del", v: x[i++] });
      } else {
        out.push({ op: "ins", v: y[j++] });
      }
    }
  }
  out.push(...a.slice(a.length - suf).map((v) => ({ op: "eq" as const, v })));
  return out;
}

const tokens = (s: string) => s.match(/\s+|[\p{L}\p{N}_]+|[^\s\p{L}\p{N}_]/gu) ?? [];

/** Word diff of two strings, adjacent tokens of one kind joined. */
export function wordDiff(before: string, after: string): Part[] {
  const parts: Part[] = [];
  for (const { op, v } of diffSeq(tokens(before), tokens(after))) {
    const last = parts[parts.length - 1];
    if (last?.op === op) last.text += v;
    else parts.push({ op, text: v });
  }
  return parts;
}

/** How much of two lines is shared, 0–1: whether a change is an edit or a replacement. */
function similarity(parts: Part[]) {
  let same = 0;
  let all = 0;
  for (const p of parts) {
    if (!p.text.trim()) continue;
    all += p.op === "eq" ? 2 * p.text.length : p.text.length;
    if (p.op === "eq") same += 2 * p.text.length;
  }
  return all ? same / all : 1;
}

/** Line diff; a removed line followed by an added one that resembles it is shown as one edited line. */
export function lineDiff(before: string, after: string): Row[] {
  const ops = diffSeq(before.split("\n"), after.split("\n"));
  const rows: Row[] = [];
  for (let k = 0; k < ops.length; ) {
    if (ops[k].op === "eq") {
      rows.push({ kind: "same", text: ops[k++].v });
      continue;
    }
    const dels: string[] = [];
    const ins: string[] = [];
    while (k < ops.length && ops[k].op !== "eq") (ops[k].op === "del" ? dels : ins).push(ops[k++].v);
    const pairs = Math.min(dels.length, ins.length);
    for (let p = 0; p < pairs; p++) {
      const parts = wordDiff(dels[p], ins[p]);
      if (similarity(parts) >= 0.4) rows.push({ kind: "mod", parts });
      else rows.push({ kind: "del", text: dels[p] }, { kind: "ins", text: ins[p] });
    }
    for (const d of dels.slice(pairs)) rows.push({ kind: "del", text: d });
    for (const i of ins.slice(pairs)) rows.push({ kind: "ins", text: i });
  }
  return rows;
}

const headingRe = /^(#{1,6})\s+(.*)$/;

/** A heading that starts an item: `<ID> Title`, the ID like `REQ-1-2`. */
const itemHeadingRe = /^\[?[A-Z][A-Z0-9]*-\d+(?:[-.]\d+)*(?:[\s:\]]|$)/;

/**
 * The markdown of one item: from its heading (`<id> Title`, possibly a link)
 * to the next heading of the same or a higher level, or the next item's
 * heading (a child item is an item of its own). Null if it is not there.
 */
export function itemSection(md: string, id: string): string | null {
  const lines = md.split("\n");
  const start = lines.findIndex((l) => {
    const m = headingRe.exec(l);
    if (!m) return false;
    const t = m[2].replace(/^\[/, "").trim();
    return t === id || t.startsWith(`${id} `) || t.startsWith(`${id}]`) || t.startsWith(`${id}:`);
  });
  if (start < 0) return null;
  const level = headingRe.exec(lines[start])![1].length;
  let end = start + 1;
  while (end < lines.length) {
    const m = headingRe.exec(lines[end]);
    if (m && (m[1].length <= level || itemHeadingRe.test(m[2].trim()))) break;
    end++;
  }
  return decodeEntities(
    lines
      .slice(start, end)
      .filter((l) => l.trim() !== "<empty-block/>")
      .join("\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim(),
  );
}

const NAMED: Record<string, string> = { amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: "\u00a0" };

/** `&#32;`, `&amp;` … as text (medtech-os-sync escapes some characters). */
export function decodeEntities(s: string): string {
  return s.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, e: string) => {
    if (e[0] !== "#") return NAMED[e.toLowerCase()] ?? m;
    const n = e[1].toLowerCase() === "x" ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
    return Number.isFinite(n) && n > 0 && n < 0x110000 ? String.fromCodePoint(n) : m;
  });
}
